import { defineStore } from "pinia";

const STORAGE_KEY = "quiz_review_bookmarks";

const safeParse = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const loadFromStorage = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? safeParse(raw) : [];
  } catch {
    return [];
  }
};

const persistToStorage = (payload) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage failures (e.g. quota exceeded, private mode)
  }
};

export const useReviewBookmarkStore = defineStore("reviewBookmarks", {
  state: () => ({
    bookmarks: loadFromStorage(),
    _hydrated: false,
    _listenerAttached: false,
  }),
  getters: {
    bookmarkList: (state) => state.bookmarks,
  },
  actions: {
    hydrate() {
      if (this._hydrated) return;
      this.bookmarks = loadFromStorage();
      this._hydrated = true;

      if (typeof window !== "undefined" && !this._listenerAttached) {
        window.addEventListener("storage", (event) => {
          if (event.key !== STORAGE_KEY) return;
          this.bookmarks = event.newValue ? safeParse(event.newValue) : [];
        });
        this._listenerAttached = true;
      }
    },
    isBookmarked(startId) {
      if (!startId) return false;
      const id = String(startId);
      return this.bookmarks.some((item) => item.startId === id);
    },
    addBookmark(entry) {
      if (!entry?.startId) return;
      const startId = String(entry.startId);
      const exists = this.bookmarks.findIndex((item) => item.startId === startId);
      const normalized = {
        ...entry,
        startId,
        bookmarkedAt: entry.bookmarkedAt || new Date().toISOString(),
      };
      if (exists !== -1) {
        this.bookmarks.splice(exists, 1, normalized);
      } else {
        this.bookmarks.unshift(normalized);
      }
      persistToStorage(this.bookmarks);
    },
    updateBookmark(startId, patch = {}) {
      if (!startId) return;
      const id = String(startId);
      const idx = this.bookmarks.findIndex((item) => item.startId === id);
      if (idx === -1) return;
      const existing = this.bookmarks[idx];
      const updated = { ...existing, ...patch, startId: id, bookmarkedAt: existing.bookmarkedAt };
      this.bookmarks.splice(idx, 1, updated);
      persistToStorage(this.bookmarks);
    },
    removeBookmark(startId) {
      if (!startId) return;
      const id = String(startId);
      const idx = this.bookmarks.findIndex((item) => item.startId === id);
      if (idx === -1) return;
      this.bookmarks.splice(idx, 1);
      persistToStorage(this.bookmarks);
    },
    toggleBookmark(entry) {
      if (!entry?.startId) return;
      const startId = String(entry.startId);
      if (this.isBookmarked(startId)) {
        this.removeBookmark(startId);
      } else {
        this.addBookmark(entry);
      }
    },
    clearAll() {
      this.bookmarks = [];
      persistToStorage(this.bookmarks);
    },
  },
});
